/* eslint-disable */
import React, { useState } from 'react';
import {
  Sparkles,
  Wand2,
  FileText,
  Globe,
  Copy,
  Layers,
  HelpCircle,
  BarChart2,
  Languages,
  MousePointerClick,
  Star,
} from 'lucide-react';
import axiosClient from '../api/axiosClient';
import toast from 'react-hot-toast';

const AI_ACTIONS = [
  { id: 'generate_page_content', name: 'Page Content & Blocks', icon: Layers, desc: 'Generate complete hero, benefits, and section copy' },
  { id: 'rewrite', name: 'Rewrite & Polish Text', icon: Wand2, desc: 'Improve clarity, authority, and B2B persuasiveness' },
  { id: 'generate_seo', name: 'SEO Meta Tags & Snippet', icon: Globe, desc: 'Generate meta title, description, and keywords' },
  { id: 'generate_faqs', name: 'Dynamic FAQs', icon: HelpCircle, desc: 'Generate common customer questions & answers' },
  { id: 'generate_blog', name: 'Full Article Draft', icon: FileText, desc: 'Draft comprehensive thought leadership articles' },
  { id: 'summarize_analytics', name: 'Summarize Analytics', icon: BarChart2, desc: 'Generate executive summary of website traffic' },
  { id: 'suggest_cta', name: 'Suggest High-Converting CTAs', icon: MousePointerClick, desc: 'Generate compelling button & hero call-to-actions' },
  { id: 'generate_testimonials', name: 'Testimonials Placeholders', icon: Star, desc: 'Generate realistic industry customer reviews' },
  { id: 'translate', name: 'Translate Page Copy', icon: Languages, desc: 'Translate content into target language' },
];

export default function AiAssistantPage() {
  const [prompt, setPrompt] = useState('');
  const [action, setAction] = useState('generate_page_content');
  const [targetLanguage, setTargetLanguage] = useState('Spanish');
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);

  const handleGenerate = async () => {
    if (!prompt && action !== 'summarize_analytics') {
      toast.error('Please specify what topic or prompt you want AI to generate.');
      return;
    }
    setLoading(true);
    try {
      const res = await axiosClient.post('/ai/generate', {
        action,
        prompt: prompt || 'Managed IT & Cybersecurity Services',
        targetLanguage,
      });
      setOutput(res.data.output);
      toast.success('AI content generated successfully!');
    } catch (err) {
      toast.error('AI generation failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(output);
    toast.success('Content copied to clipboard!');
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div>
        <div className="flex items-center space-x-3">
          <div className="p-2.5 rounded-xl bg-purple-600/20 border border-purple-500/30 text-purple-400">
            <Sparkles size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white tracking-tight">AI Content Assistant & Studio</h1>
            <p className="text-xs text-slate-400 mt-0.5">
              Draft full articles, generate SEO titles, create FAQs, and rewrite web copy automatically
            </p>
          </div>
        </div>
      </div>

      {/* Action Selector Pills */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-3">
        {AI_ACTIONS.map((act) => {
          const Icon = act.icon;
          const isSelected = action === act.id;
          return (
            <button
              key={act.id}
              onClick={() => setAction(act.id)}
              className={`p-3.5 rounded-2xl border text-left transition-all ${
                isSelected
                  ? 'bg-purple-950/40 border-purple-500 ring-1 ring-purple-500/40'
                  : 'bg-slate-900 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center space-x-2 mb-1">
                <Icon size={16} className={isSelected ? 'text-purple-400' : 'text-slate-400'} />
                <span className={`text-xs font-bold ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                  {act.name}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 line-clamp-1">{act.desc}</p>
            </button>
          );
        })}
      </div>

      {/* Generator & Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4 shadow-xl flex flex-col justify-between">
          <div className="space-y-4">
            {action === 'translate' && (
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-1">Target Language</label>
                <select
                  value={targetLanguage}
                  onChange={(e) => setTargetLanguage(e.target.value)}
                  className="w-full p-2.5 rounded-xl border border-slate-800 bg-slate-950 text-white text-xs"
                >
                  <option value="Spanish">Spanish (Español)</option>
                  <option value="French">French (Français)</option>
                  <option value="German">German (Deutsch)</option>
                  <option value="Arabic">Arabic (العربية)</option>
                  <option value="Japanese">Japanese (日本語)</option>
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-300 mb-1">Topic, Prompt, or Context</label>
              <textarea
                rows={7}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Write a compelling page overview for Cloud Migration & Disaster Recovery for Healthcare clinics..."
                className="w-full p-3 rounded-xl border border-slate-800 bg-slate-950 text-white text-xs focus:outline-none focus:border-purple-500 leading-relaxed placeholder-slate-500"
              />
            </div>
          </div>

          <button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full py-3.5 rounded-xl font-bold text-xs bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white shadow-xl shadow-purple-600/30 flex items-center justify-center space-x-2 transition-all cursor-pointer"
          >
            <Sparkles size={16} />
            <span>{loading ? 'Generating with AI...' : 'Generate with AI Assistant'}</span>
          </button>
        </div>

        {/* Output */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-3">
              <h3 className="font-bold text-sm text-white">AI Output Preview</h3>
              {output && (
                <button
                  onClick={handleCopy}
                  className="text-xs text-purple-400 hover:text-purple-300 flex items-center space-x-1 font-bold transition-colors"
                >
                  <Copy size={14} />
                  <span>Copy Content</span>
                </button>
              )}
            </div>

            <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 font-mono text-xs text-slate-300 min-h-[220px] max-h-[360px] overflow-y-auto whitespace-pre-wrap leading-relaxed custom-scrollbar">
              {output || 'Generated content will appear here...'}
            </div>
          </div>

          {output && (
            <div className="pt-3 border-t border-slate-800 flex items-center justify-between text-xs text-slate-400">
              <span>Ready for copy & paste into CMS</span>
              <button
                onClick={handleCopy}
                className="px-3 py-1 rounded-lg bg-purple-600/20 text-purple-300 hover:bg-purple-600/30 font-bold"
              >
                Copy to Clipboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
